/**
 * File → base64 helpers for the incorporation E-Filing screen. Freshly attached
 * documents are held client-side (base64) and uploaded server-side at submit.
 */
import type { EfDocRef } from './incorporationEfilingModel';

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED = ['application/pdf'];

/** Validate a picked file. Returns an error message, or '' when OK. */
export function validateEfUploadFile(file: File): string {
  if (!file) return 'No file selected.';
  const type = (file.type || '').toLowerCase();
  const isPdf = type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (ALLOWED.length && !isPdf) return 'Only PDF files are allowed.';
  if (file.size > MAX_BYTES) return 'File exceeds the 10 MB limit.';
  if ((file.name || '').length > 500) return 'File name is too long.';
  return '';
}

/** Read a File into a base64 body (no `data:` prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] || '');
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

/** Build an {@link EfDocRef} (pending, base64) from a picked file. */
export async function makeDocRefFromFile(file: File): Promise<EfDocRef> {
  const contentBase64 = await fileToBase64(file);
  return {
    documentFileName: file.name,
    type: file.type || 'application/pdf',
    size: file.size,
    contentBase64,
    fileCd: null,
    documentId: null,
  };
}
